import React, { useState, useEffect } from 'react';
import { 
  Settings as SettingsIcon, 
  Users, 
  Building, 
  Bell, 
  Shield, 
  Plus, 
  Edit2, 
  Trash2,
  Save,
  X,
  UploadCloud
} from 'lucide-react';
import { authAPI, pdfAPI } from '../services/api';
import { User, Hotel, CreateUserRequest, UpdateHotelRequest } from '../types';
import { DEFAULT_CHECK_IN_WELCOME_TEMPLATE } from '../constants/messaging';

interface SettingsProps {
  currentUser: User;
  hotel: Hotel;
  onHotelUpdate: (hotel: Hotel) => void;
}

const Settings: React.FC<SettingsProps> = ({ currentUser, hotel, onHotelUpdate }) => {
  const [activeTab, setActiveTab] = useState('hotel');
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pdfs, setPdfs] = useState<any[]>([]);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfUploading, setPdfUploading] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  
  // Hotel form state
  const [hotelForm, setHotelForm] = useState<UpdateHotelRequest>({
    name: hotel.name,
    address: hotel.address,
    phone: hotel.phone,
    email: hotel.email,
    description: hotel.description,
    settings: {
      ...hotel.settings,
      check_in_welcome_template:
        hotel.settings?.check_in_welcome_template ?? DEFAULT_CHECK_IN_WELCOME_TEMPLATE,
    },
  });

  // User management state
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [userForm, setUserForm] = useState<CreateUserRequest>({
    name: '',
    email: '',
    password: '',
    role: 'staff',
    hotel_id: hotel.id,
  });

  const tabs = [
    { id: 'hotel', label: 'Hotel Profile', icon: Building },
    { id: 'users', label: 'User Management', icon: Users },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'content', label: 'Content & PDFs', icon: UploadCloud },
    { id: 'security', label: 'Security', icon: Shield },
  ];

  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
    }
    if (activeTab === 'content') {
      fetchPdfs();
    }
  }, [activeTab]);

  const fetchUsers = async () => {
    try {
      const usersData = await authAPI.getUsers();
      setUsers(usersData);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleHotelSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const updatedHotel = await authAPI.updateHotel(hotelForm);
      onHotelUpdate(updatedHotel);
      setSuccess('Hotel information updated successfully');
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to update hotel information');
    } finally {
      setLoading(false);
    }
  };

  const handleUserSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (editingUser) {
        const { password, hotel_id, ...updateData } = userForm;
        await authAPI.updateUser(editingUser.id, updateData as any);
      } else {
        await authAPI.createUser(userForm);
      }
      
      await fetchUsers();
      setShowUserModal(false);
      setEditingUser(null);
      resetUserForm();
      setSuccess(editingUser ? 'User updated successfully' : 'User created successfully');
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to save user');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      await authAPI.deleteUser(userId);
      await fetchUsers();
      setSuccess('User deleted successfully');
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to delete user');
    }
  };

  const fetchPdfs = async () => {
    setPdfLoading(true);
    setError('');
    try {
      const data: any = await pdfAPI.getPdfs();
      // API returns { success, files, total } or array; normalize
      const files = Array.isArray(data) ? data : data?.files ?? [];
      setPdfs(files);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch PDFs');
    } finally {
      setPdfLoading(false);
    }
  };

  const handleUploadPdf = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPdfUploading(true);
    setError('');
    setSuccess('');
    try {
      await pdfAPI.uploadPdf(file, 'general');
      await fetchPdfs();
      setSuccess('PDF uploaded; embeddings finished (background job on server).');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to upload PDF');
    } finally {
      setPdfUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeletePdf = async (filename: string) => {
    if (!window.confirm(`Delete ${filename}? This will remove its embeddings.`)) return;
    setError('');
    setSuccess('');
    try {
      await pdfAPI.deletePdf(filename);
      await fetchPdfs();
      setSuccess('PDF deleted.');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete PDF');
    }
  };

  const resetUserForm = () => {
    setUserForm({
      name: '',
      email: '',
      password: '',
      role: 'staff',
      hotel_id: hotel.id,
    });
  };

  const openUserModal = (user?: User) => {
    if (user) {
      setEditingUser(user);
      setUserForm({
        name: user.name,
        email: user.email,
        password: '',
        role: user.role,
        hotel_id: user.hotel_id,
      });
    } else {
      setEditingUser(null);
      resetUserForm();
    }
    setShowUserModal(true);
  };

  const renderHotelSettings = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-black-guestby">Hotel Profile</h3>
        <button
          onClick={handleHotelSubmit}
          disabled={loading}
          className="flex items-center px-4 py-2 bg-blue-guestby text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          <Save className="h-4 w-4 mr-2" />
          {loading ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      <form onSubmit={handleHotelSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-black-guestby mb-2">
              Hotel Name
            </label>
            <input
              type="text"
              value={hotelForm.name || ''}
              onChange={(e) => setHotelForm(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-guestby"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-black-guestby mb-2">
              Email
            </label>
            <input
              type="email"
              value={hotelForm.email || ''}
              onChange={(e) => setHotelForm(prev => ({ ...prev, email: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-guestby"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-black-guestby mb-2">
              Phone
            </label>
            <input
              type="tel"
              value={hotelForm.phone || ''}
              onChange={(e) => setHotelForm(prev => ({ ...prev, phone: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-guestby"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-black-guestby mb-2">
              Address
            </label>
            <input
              type="text"
              value={hotelForm.address || ''}
              onChange={(e) => setHotelForm(prev => ({ ...prev, address: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-guestby"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-black-guestby mb-2">
              Description
            </label>
            <textarea
              value={hotelForm.description || ''}
              onChange={(e) => setHotelForm(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-guestby"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-black-guestby mb-2">
              Check-in WhatsApp message (template)
            </label>
            <p className="text-xs text-grey-guestby mb-2">
              Sent when a guest is marked checked in. Not generated by AI. Placeholders:{' '}
              <code className="bg-slate-100 px-1 rounded">{'{{hotel_name}}'}</code>,{' '}
              <code className="bg-slate-100 px-1 rounded">{'{{guest_name}}'}</code>,{' '}
              <code className="bg-slate-100 px-1 rounded">{'{{room_number}}'}</code>,{' '}
              <code className="bg-slate-100 px-1 rounded">{'{{first_name}}'}</code>.
            </p>
            <textarea
              value={hotelForm.settings?.check_in_welcome_template || ''}
              onChange={(e) =>
                setHotelForm((prev) => ({
                  ...prev,
                  settings: {
                    ...prev.settings!,
                    check_in_welcome_template: e.target.value,
                  },
                }))
              }
              rows={5}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-guestby text-sm"
            />
          </div>
        </div>

        {/* Hotel Settings */}
        <div className="border-t pt-6">
          <h4 className="text-md font-medium text-black-guestby mb-4">System Settings</h4>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-black-guestby">Allow Guest Registration</label>
                <p className="text-xs text-grey-guestby">Allow guests to register themselves via WhatsApp</p>
              </div>
              <input
                type="checkbox"
                checked={hotelForm.settings?.allow_guest_registration || false}
                onChange={(e) => setHotelForm(prev => ({
                  ...prev,
                  settings: { ...prev.settings, allow_guest_registration: e.target.checked }
                }))}
                className="h-4 w-4 text-blue-guestby focus:ring-blue-guestby border-gray-300 rounded"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-black-guestby">Auto-assign Rooms</label>
                <p className="text-xs text-grey-guestby">Automatically assign available rooms to new guests</p>
              </div>
              <input
                type="checkbox"
                checked={hotelForm.settings?.auto_assign_rooms || false}
                onChange={(e) => setHotelForm(prev => ({
                  ...prev,
                  settings: { ...prev.settings, auto_assign_rooms: e.target.checked }
                }))}
                className="h-4 w-4 text-blue-guestby focus:ring-blue-guestby border-gray-300 rounded"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-black-guestby">WhatsApp Integration</label>
                <p className="text-xs text-grey-guestby">Enable WhatsApp messaging for guests</p>
              </div>
              <input
                type="checkbox"
                checked={hotelForm.settings?.whatsapp_enabled || false}
                onChange={(e) => setHotelForm(prev => ({
                  ...prev,
                  settings: { ...prev.settings, whatsapp_enabled: e.target.checked }
                }))}
                className="h-4 w-4 text-blue-guestby focus:ring-blue-guestby border-gray-300 rounded"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-black-guestby">AI Concierge</label>
                <p className="text-xs text-grey-guestby">Enable AI-powered guest assistance</p>
              </div>
              <input
                type="checkbox"
                checked={hotelForm.settings?.ai_concierge_enabled || false}
                onChange={(e) => setHotelForm(prev => ({
                  ...prev,
                  settings: { ...prev.settings, ai_concierge_enabled: e.target.checked }
                }))}
                className="h-4 w-4 text-blue-guestby focus:ring-blue-guestby border-gray-300 rounded"
              />
            </div>
          </div>
        </div>
      </form>
    </div>
  );

  const renderUserManagement = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-black-guestby">User Management</h3>
        {currentUser.role === 'admin' && (
          <button
            onClick={() => openUserModal()}
            className="flex items-center px-4 py-2 bg-blue-guestby text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add User
          </button>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Last Login
              </th>
              {currentUser.role === 'admin' && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.map((user) => (
              <tr key={user.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-black-guestby">{user.name}</div>
                    <div className="text-sm text-grey-guestby">{user.email}</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    user.role === 'admin' 
                      ? 'bg-purple-100 text-purple-800' 
                      : 'bg-blue-100 text-blue-800'
                  }`}>
                    {user.role}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    user.is_active 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {user.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-grey-guestby">
                  {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                </td>
                {currentUser.role === 'admin' && (
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => openUserModal(user)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      {user.id !== currentUser.id && (
                        <button
                          onClick={() => handleDeleteUser(user.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderNotificationSettings = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-black-guestby">Notification Preferences</h3>
      
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <label className="text-sm font-medium text-black-guestby">Email Notifications</label>
            <p className="text-xs text-grey-guestby">Receive notifications via email</p>
          </div>
          <input
            type="checkbox"
            checked={hotelForm.settings?.notification_preferences?.email || false}
            onChange={(e) => setHotelForm(prev => ({
              ...prev,
              settings: {
                ...prev.settings,
                notification_preferences: {
                  email: e.target.checked,
                  sms: prev.settings?.notification_preferences?.sms || false,
                  whatsapp: prev.settings?.notification_preferences?.whatsapp || false
                }
              }
            }))}
            className="h-4 w-4 text-blue-guestby focus:ring-blue-guestby border-gray-300 rounded"
          />
        </div>

        <div className="flex items-center justify-between">
          <div>
            <label className="text-sm font-medium text-black-guestby">SMS Notifications</label>
            <p className="text-xs text-grey-guestby">Receive notifications via SMS</p>
          </div>
          <input
            type="checkbox"
            checked={hotelForm.settings?.notification_preferences?.sms || false}
            onChange={(e) => setHotelForm(prev => ({
              ...prev,
              settings: {
                ...prev.settings,
                notification_preferences: {
                  email: prev.settings?.notification_preferences?.email || false,
                  sms: e.target.checked,
                  whatsapp: prev.settings?.notification_preferences?.whatsapp || false
                }
              }
            }))}
            className="h-4 w-4 text-blue-guestby focus:ring-blue-guestby border-gray-300 rounded"
          />
        </div>

        <div className="flex items-center justify-between">
          <div>
            <label className="text-sm font-medium text-black-guestby">WhatsApp Notifications</label>
            <p className="text-xs text-grey-guestby">Receive notifications via WhatsApp</p>
          </div>
          <input
            type="checkbox"
            checked={hotelForm.settings?.notification_preferences?.whatsapp || false}
            onChange={(e) => setHotelForm(prev => ({
              ...prev,
              settings: {
                ...prev.settings,
                notification_preferences: {
                  email: prev.settings?.notification_preferences?.email || false,
                  sms: prev.settings?.notification_preferences?.sms || false,
                  whatsapp: e.target.checked
                }
              }
            }))}
            className="h-4 w-4 text-blue-guestby focus:ring-blue-guestby border-gray-300 rounded"
          />
        </div>
      </div>

      <button
        onClick={handleHotelSubmit}
        disabled={loading}
        className="flex items-center px-4 py-2 bg-blue-guestby text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
      >
        <Save className="h-4 w-4 mr-2" />
        {loading ? 'Saving...' : 'Save Preferences'}
      </button>
    </div>
  );

  const renderContentSettings = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-black-guestby">Content & PDFs</h3>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={pdfUploading}
            className="flex items-center px-4 py-2 bg-blue-guestby text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <UploadCloud className="h-4 w-4 mr-2" />
            {pdfUploading ? 'Uploading...' : 'Upload PDF'}
          </button>
          <input
            type="file"
            accept="application/pdf"
            ref={fileInputRef}
            className="hidden"
            onChange={handleUploadPdf}
          />
        </div>
      </div>

      {pdfLoading ? (
        <p className="text-sm text-grey-guestby">Loading PDFs...</p>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  File
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Size
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Modified
                </th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {pdfs.length === 0 && (
                <tr>
                  <td className="px-6 py-4 text-sm text-grey-guestby" colSpan={4}>
                    No PDFs uploaded yet. Add menu, policies, spa/amenities to power the concierge.
                  </td>
                </tr>
              )}
              {pdfs.map((file: any, idx: number) => (
                <tr key={idx}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-black-guestby">
                    {file.filename || file.name || 'PDF'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-grey-guestby">
                    {file.sizeFormatted || `${Math.round((file.size || 0) / 1024 / 1024 * 100) / 100} MB`}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-grey-guestby">
                    {file.modified ? new Date(file.modified).toLocaleString() : ''}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleDeletePdf(file.filename || file.name)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderSecuritySettings = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-black-guestby">Security Settings</h3>
      
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h4 className="font-medium text-yellow-800 mb-2">Security Features</h4>
        <ul className="text-sm text-yellow-700 space-y-1">
          <li>• All passwords are encrypted using bcrypt</li>
          <li>• JWT tokens expire after 24 hours</li>
          <li>• API requests are rate-limited</li>
          <li>• All data is isolated by hotel</li>
        </ul>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-800 mb-2">Password Policy</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Minimum 8 characters</li>
          <li>• Regular password updates recommended</li>
          <li>• Use unique passwords for each account</li>
        </ul>
      </div>
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-black-guestby flex items-center">
          <SettingsIcon className="h-8 w-8 mr-3" />
          Settings
        </h1>
      </div>

      {(error || success) && (
        <div className={`px-4 py-3 rounded-lg ${
          error ? 'bg-red-50 border border-red-200 text-red-700' : 'bg-green-50 border border-green-200 text-green-700'
        }`}>
          {error || success}
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar */}
        <div className="lg:w-64">
          <nav className="space-y-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center px-4 py-3 text-left rounded-lg transition-colors ${
                  activeTab === tab.id
                    ? 'bg-blue-guestby text-white'
                    : 'text-grey-guestby hover:bg-gray-100'
                }`}
              >
                <tab.icon className="h-5 w-5 mr-3" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1 bg-white rounded-lg shadow-md border border-gray-200 p-6">
          {activeTab === 'hotel' && renderHotelSettings()}
          {activeTab === 'users' && renderUserManagement()}
          {activeTab === 'notifications' && renderNotificationSettings()}
          {activeTab === 'content' && renderContentSettings()}
          {activeTab === 'security' && renderSecuritySettings()}
        </div>
      </div>

      {/* User Modal */}
      {showUserModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-black-guestby">
                {editingUser ? 'Edit User' : 'Add New User'}
              </h3>
              <button
                onClick={() => setShowUserModal(false)}
                className="text-grey-guestby hover:text-black-guestby"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleUserSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-black-guestby mb-2">
                  Name
                </label>
                <input
                  type="text"
                  required
                  value={userForm.name}
                  onChange={(e) => setUserForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-guestby"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-black-guestby mb-2">
                  Email
                </label>
                <input
                  type="email"
                  required
                  value={userForm.email}
                  onChange={(e) => setUserForm(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-guestby"
                />
              </div>

              {!editingUser && (
                <div>
                  <label className="block text-sm font-medium text-black-guestby mb-2">
                    Password
                  </label>
                  <input
                    type="password"
                    required
                    value={userForm.password}
                    onChange={(e) => setUserForm(prev => ({ ...prev, password: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-guestby"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-black-guestby mb-2">
                  Role
                </label>
                <select
                  value={userForm.role}
                  onChange={(e) => setUserForm(prev => ({ ...prev, role: e.target.value as 'admin' | 'staff' }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-guestby"
                >
                  <option value="staff">Staff</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowUserModal(false)}
                  className="px-4 py-2 text-grey-guestby hover:text-black-guestby"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-guestby text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? 'Saving...' : (editingUser ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
